import { z } from "zod";

export const timestampSchema = z.iso
  .datetime({ offset: true })
  .regex(
    /^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/,
  );

export type Timestamp = z.infer<typeof timestampSchema>;

export const resourceRefSchema = z.strictObject({
  kind: z.string().regex(/^[a-z][a-z0-9_.-]*$/),
  externalId: z.string().min(1),
});

export type ResourceRef = z.infer<typeof resourceRefSchema>;

export const socialLinkSchema = z.strictObject({
  provider: z.string().min(1),
  url: z.url().regex(/^https?:\/\//),
});

export type SocialLink = z.infer<typeof socialLinkSchema>;

export const profilePatchSchema = z
  .strictObject({
    displayName: z.string().nullable().optional(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    bio: z.string().nullable().optional(),
    company: z.string().nullable().optional(),
    location: z.string().nullable().optional(),
    publicEmail: z.string().nullable().optional(),
    // Addresses the person has committed under, most recently used first.
    commitEmails: z.array(z.string()).optional(),
    website: z.string().nullable().optional(),
    socialLinks: z.array(socialLinkSchema).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, "Provide at least one profile field.");

export type ProfilePatch = z.infer<typeof profilePatchSchema>;

export const sourceIdentitySchema = z.strictObject({
  externalId: z.string().min(1),
  accountKind: z.enum(["person", "bot", "organization", "unknown"]),
  handle: z.string().nullable(),
  url: z
    .url()
    .regex(/^https?:\/\//)
    .nullable(),
  avatarUrl: z
    .url()
    .regex(/^https?:\/\//)
    .nullable(),
  observedAt: timestampSchema,
  firstSeenAt: timestampSchema.optional(),
  profile: profilePatchSchema.optional(),
});

export type SourceIdentity = z.infer<typeof sourceIdentitySchema>;

export const sourceResourceSchema = z.strictObject({
  externalId: z.string().min(1),
  kind: z.string().regex(/^[a-z][a-z0-9_.-]*$/),
  title: z.string(),
  url: z.url().regex(/^https?:\/\//),
  avatarUrl: z
    .url()
    .regex(/^https?:\/\//)
    .nullable(),
  observedAt: timestampSchema,
});

export type SourceResource = z.infer<typeof sourceResourceSchema>;

export const sourceActivitySchema = z.strictObject({
  externalId: z.string().min(1),
  signal: z.string().regex(/^[a-z][a-z0-9_-]*(\.[a-z][a-z0-9_-]*)+$/),
  actorExternalId: z.string().min(1).nullable(),
  subject: z.union([resourceRefSchema, z.null()]),
  scope: z.union([resourceRefSchema, z.null()]),
  title: z.string(),
  body: z.string(),
  url: z.url().regex(/^https?:\/\//),
  occurredAt: z.union([timestampSchema, z.null()]),
  observedAt: timestampSchema,
  baseline: z.boolean(),
  subjectIdentityExternalId: z.string().min(1).nullable().optional(),
});

export type SourceActivity = z.infer<typeof sourceActivitySchema>;

export const ingestionBatchSchema = z.strictObject({
  schemaVersion: z.literal(1),
  provider: z.string().regex(/^[a-z][a-z0-9_-]*$/),
  runId: z.uuid(),
  batchId: z.string().min(1),
  identities: z.array(sourceIdentitySchema),
  resources: z.array(sourceResourceSchema),
  activities: z.array(sourceActivitySchema),
});

export type IngestionBatch = z.infer<typeof ingestionBatchSchema>;

export const ingestionReceiptSchema = z.strictObject({
  runId: z.uuid(),
  batchId: z.string().min(1),
  createdActivities: z.number().int().min(0),
  identityIds: z.record(z.string(), z.uuid()),
  resourceIds: z.record(z.string(), z.uuid()),
  activityIds: z.record(z.string(), z.uuid()),
});

export type IngestionReceipt = z.infer<typeof ingestionReceiptSchema>;

export class ContractError extends Error {
  constructor(
    public issues: Array<{ path: string; message?: string }>,
    contract = "ingestion",
  ) {
    super(`The payload does not match ${contract} contract v1.`);
    this.name = "ContractError";
  }
}
function parse<S extends z.ZodType>(schema: S, value: unknown, contract: string): z.output<S> {
  const result = schema.safeParse(value);
  if (!result.success)
    throw new ContractError(
      result.error.issues.map((i) => ({
        path: "/" + i.path.join("/"),
        message: i.message,
      })),
      contract,
    );
  return result.data;
}
export const parseIngestionBatch = (value: unknown) =>
  parse(ingestionBatchSchema, value, "ingestion");
export function parseIngestionReceipt(value: unknown): IngestionReceipt {
  parse(ingestionReceiptSchema, value, "ingestion-receipt");
  // Provider IDs are opaque strings. Preserve keys such as __proto__ that Zod omits when cloning records.
  const receipt = value as IngestionReceipt;
  for (const ids of [receipt.identityIds, receipt.resourceIds, receipt.activityIds])
    for (const id of Object.values(ids)) parse(z.uuid(), id, "ingestion-receipt");
  return receipt;
}
